import { useState } from 'react';
import { useSetRecoilState } from 'recoil';
import { dataVersionState } from '../../state.js';
import { api } from '../../api.js';
import { useApi } from '../../hooks.js';
import { Card } from '../Card.js';
import { ErrorBanner, SubmitButton } from '../FormBits.js';
import { fmtNum } from '../../utils.js';

interface BenchmarkStatus {
  observations: number;
  indices: number;
  from_date: string | null;
  to_date: string | null;
  last_upload: string | null;
}

interface BenchmarkUploadResult {
  valuesNew: number;
  valuesExisting: number;
  indexCount: number;
  dateRange: { from: string | null; to: string | null };
  clientsRecomputed: Record<string, number>;
}

/** ONE system-wide benchmark index store. Uploads merge — existing observations
 * are never overridden; all clients' benchmark series recompute automatically. */
export function SystemBenchmarkCard() {
  const bump = useSetRecoilState(dataVersionState);
  const { data: status } = useApi<BenchmarkStatus>('/admin/benchmark/status');
  const [file, setFile] = useState<File | null>(null);
  const [busy, setBusy] = useState(false);
  const [res, setRes] = useState<BenchmarkUploadResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [formKey, setFormKey] = useState(0);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!file) return;
    setBusy(true); setError(null); setRes(null);
    try {
      const form = new FormData();
      form.set('file', file);
      setRes(await api.upload<BenchmarkUploadResult>('/admin/benchmark', form));
      bump((v) => v + 1);
      setFile(null);
      setFormKey((k) => k + 1);
    } catch (err) {
      setError(String((err as Error).message));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card title="מדדי ייחוס — מאגר מערכתי (אחד לכל הלקוחות)">
      <p className="mb-3 text-sm text-muted-foreground">
        קובץ המדדים נמשך ממערכת חיצונית ומועלה כל כמה ימים. העלאה ממזגת תצפיות חדשות בלבד —
        נתונים קיימים לעולם אינם נדרסים; סדרות מדד הייחוס של כל הלקוחות מחושבות מחדש אוטומטית.
      </p>
      {status && (
        <div className="mb-4 grid grid-cols-2 gap-3 md:grid-cols-4">
          <Stat label="תצפיות" value={fmtNum(status.observations)} />
          <Stat label="מדדים" value={fmtNum(status.indices)} />
          <Stat label="טווח תאריכים" value={status.from_date ? `${status.from_date} → ${status.to_date}` : '—'} />
          <Stat label="העלאה אחרונה" value={status.last_upload ? new Date(status.last_upload).toLocaleString('he-IL') : '—'} />
        </div>
      )}
      <form key={formKey} className="flex max-w-2xl items-end gap-3" onSubmit={submit}>
        <fieldset disabled={busy} className="contents">
        <label className="flex-1 text-sm">
          <span className="mb-1 block text-muted-foreground">קובץ מדדים (.xls / .xlsx — גיליון "תיק")</span>
          <input required type="file" accept=".xlsx,.xls,.xlsb,.csv"
            className="w-full rounded-md border border-input bg-popover px-3 py-2 file:me-3 file:rounded file:border-0 file:bg-muted file:px-3 file:py-1 file:text-foreground"
            onChange={(e) => setFile(e.target.files?.[0] ?? null)} />
        </label>
        <SubmitButton busy={busy} busyLabel="ממזג ומחשב מחדש…">העלה ומזג</SubmitButton>
        </fieldset>
      </form>
      <ErrorBanner error={error} />
      {res && (
        <p className="mt-3 text-sm">
          <span className="text-success">נוספו {fmtNum(res.valuesNew)} תצפיות חדשות</span>
          {' · '}{fmtNum(res.valuesExisting)} כבר היו קיימות (לא נדרסו)
          {' · '}חושבו מחדש: {Object.entries(res.clientsRecomputed).map(([c, n]) => `${c} (${fmtNum(n)})`).join(', ') || '—'}
        </p>
      )}
    </Card>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-lg border border-border p-3">
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className="text-sm font-semibold tabular-nums" dir="ltr">{value}</div>
    </div>
  );
}
